import { useEffect, useState } from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { Content as ContentApi, Drafts, type Draft } from '../api/client';
import { useStore } from '../store';
import { Hash, Copy as CopyIcon, FileText, Sparkles, Tag } from 'lucide-react';
import { motion } from 'framer-motion';

export function Content() {
  const [brief, setBrief] = useState('Minimalist ceramic planter, beige clay, indoor plant, soft daylight');
  const [kind, setKind] = useState<'title' | 'description' | 'tags'>('title');
  const [results, setResults] = useState<Array<Record<string, string>>>([]);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(false);
  const pushToast = useStore((s) => s.pushToast);

  useEffect(() => {
    Drafts.list().then(setDrafts).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      if (kind === 'title') {
        const r = await ContentApi.copy({ brief, kind: 'title', n: 5 });
        setResults(r as any);
      } else if (kind === 'description') {
        const r = await ContentApi.description({ brief, n: 3 });
        setResults(r as any);
      } else {
        const r = await ContentApi.tags({ brief, n: 3 });
        setResults(r as any);
      }
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    pushToast({ kind: 'success', message: 'Copied' });
  };

  return (
    <div>
      <PageHeader
        kicker="// content · 05"
        title="Generated library"
        description="All your generated copy, descriptions, and tag sets in one searchable place."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
        <div className="space-y-4">
          <Card>
            <CardHeader kicker="// gen" title="Quick generator" description="Standalone copy/desc/tags from a brief." />
            <Field label="Brief">
              <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3} className="resize-none" />
            </Field>

            <div className="mt-3">
              <div className="kicker mb-2">kind</div>
              <div className="grid grid-cols-3 gap-1.5">
                {(['title', 'description', 'tags'] as const).map((k) => (
                  <button
                    key={k}
                    onClick={() => setKind(k)}
                    className={
                      'h-10 rounded-lg text-[12px] font-medium border transition flex items-center justify-center gap-1.5 ' +
                      (kind === k
                        ? 'border-etsy bg-etsy-50 text-etsy-700 shadow-[0_0_0_3px_rgba(241,100,30,0.10)]'
                        : 'border-cream-300 bg-white text-ink-500 hover:text-ink-800 hover:border-cream-200')
                    }
                  >
                    {k === 'title' && <Sparkles size={12} />}
                    {k === 'description' && <FileText size={12} />}
                    {k === 'tags' && <Tag size={12} />}
                    {k}
                  </button>
                ))}
              </div>
            </div>

            <Button variant="primary" className="w-full mt-4" leftIcon={<Sparkles size={13} />} onClick={handleGenerate} loading={loading}>
              {loading ? 'Generating…' : 'Generate'}
            </Button>
          </Card>

          <Card noise>
            <CardHeader kicker="// drafts" title="Recent drafts" description="Full studio drafts." />
            <div className="space-y-1">
              {drafts.length === 0
                ? <div className="text-[12px] text-ink-500 px-1 py-3">No drafts yet</div>
                : drafts.slice(0, 6).map((d) => (
                    <div key={d.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-cream-100 transition">
                      <div className="h-7 w-7 rounded-lg bg-etsy-50 border border-etsy-200 flex items-center justify-center">
                        <Sparkles size={11} className="text-etsy" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] text-ink-800 truncate font-medium">{d.brief}</div>
                        <div className="kicker mt-0.5 text-ink-400">DRAFT · {d.id}</div>
                      </div>
                      <Badge tone={d.status === 'ready' ? 'success' : 'warn'}>{d.status}</Badge>
                    </div>
                  ))}
            </div>
          </Card>
        </div>

        <div className="space-y-3 min-w-0">
          {loading ? (
            <Card><Skeleton className="h-48 rounded-xl" /></Card>
          ) : results.length === 0 ? (
            <Card className="min-h-[300px] flex items-center justify-center">
              <div className="text-center">
                <div className="inline-flex h-12 w-12 rounded-2xl bg-cream-200 border border-cream-300 items-center justify-center mb-3">
                  <Sparkles size={20} className="text-ink-500" strokeWidth={1.7} />
                </div>
                <div className="text-[13px] text-ink-800 mb-1 font-medium">No results yet</div>
                <div className="text-[12px] text-ink-500">Pick a kind, hit generate.</div>
              </div>
            </Card>
          ) : (
            results.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="kicker mb-1.5">v{i + 1}</div>
                      {r.title && <div className="text-[14px] text-ink-800 tracking-tight mb-1 font-medium">{r.title}</div>}
                      {r.description && <div className="text-[12.5px] text-ink-700 tracking-tight whitespace-pre-line max-h-32 overflow-y-auto scroll-soft pr-2 leading-relaxed">{r.description}</div>}
                      {r.tags && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {r.tags.split(',').filter(Boolean).map((t) => (
                            <span key={t} className="px-2 h-6 inline-flex items-center gap-1 rounded-md bg-etsy-50 border border-etsy-200 text-[10.5px] text-etsy-700 font-mono">
                              <Hash size={9} className="opacity-60" />
                              {t.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => copy(r.title ?? r.description ?? r.tags ?? '')}
                      className="p-1.5 rounded-lg text-ink-400 hover:text-etsy hover:bg-etsy-50 transition shrink-0"
                    >
                      <CopyIcon size={12} />
                    </button>
                  </div>
                </Card>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
